"use client";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { cn } from "@/lib/utils";

import React, {
  forwardRef,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { motion, AnimatePresence, type Transition } from "framer-motion";
import { OptimizedGlass } from "../../primitives";
import { useGlassSound } from "../../utils/soundDesign";
import { useA11yId } from "../../utils/a11y";
import { useMotionPreference } from "../../hooks/useMotionPreference";
import { createGlassStyle } from "../../utils/createGlassStyle";

export type DrawingTool = "pen" | "marker" | "eraser" | "shape";

export interface DrawingPoint {
  x: number;
  y: number;
  pressure?: number;
}

export interface DrawingStroke {
  id: string;
  userId: string;
  userName: string;
  userColor: string;
  points: DrawingPoint[];
  tool: DrawingTool;
  color: string;
  size: number;
  opacity: number;
  timestamp: number;
  isComplete: boolean;
}

export interface WhiteboardUser {
  id: string;
  name: string;
  color: string;
  avatar?: string;
  cursorX: number;
  cursorY: number;
  isDrawing: boolean;
  currentTool: string;
  lastActivity: number;
}

export interface GlassSharedWhiteboardProps {
  width?: number;
  height?: number;
  users: WhiteboardUser[];
  currentUserId: string;
  strokes?: DrawingStroke[];
  backgroundColor?: string;
  gridVisible?: boolean;
  showUserCursors?: boolean;
  showToolbar?: boolean;
  showUserList?: boolean;
  showUndoRedo?: boolean;
  maxStrokes?: number;
  realTimeSync?: boolean;
  soundEnabled?: boolean;
  canDraw?: boolean;
  readOnly?: boolean;
  onStroke?: (stroke: DrawingStroke) => void;
  onClear?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  onUserCursorMove?: (userId: string, x: number, y: number) => void;
  className?: string;
}

interface ToolDefinition {
  id: DrawingTool;
  name: string;
  icon: string;
}

const tools: readonly ToolDefinition[] = [
  { id: "pen", name: "Pen", icon: "✏️" },
  { id: "marker", name: "Marker", icon: "🖍️" },
  { id: "eraser", name: "Eraser", icon: "🧽" },
  { id: "shape", name: "Shape", icon: "📐" },
];

const colors = [
  "var(--glass-black)",
  "#FF0000",
  "#00FF00",
  "#0000FF",
  "#FFFF00",
  "#FF00FF",
  "#00FFFF",
  "#FF8000",
  "#8000FF",
  "#FF0080",
];

const sizes = [2, 4, 8, 12, 16, 24] as const;

export const GlassSharedWhiteboard = forwardRef<
  HTMLDivElement,
  GlassSharedWhiteboardProps
>(
  (
    {
      width = 800,
      height = 600,
      users,
      currentUserId,
      strokes = [],
      backgroundColor = "var(--glass-white)",
      gridVisible = false,
      showUserCursors = true,
      showToolbar = true,
      showUserList = true,
      showUndoRedo = true,
      maxStrokes = 1000,
      realTimeSync = false,
      soundEnabled = true,
      canDraw = true,
      readOnly = false,
      onStroke,
      onClear,
      onUndo,
      onRedo,
      onUserCursorMove,
      className = "",
      ...props
    },
    ref
  ) => {
    const prefersReducedMotion = useReducedMotion();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentStroke, setCurrentStroke] = useState<DrawingStroke | null>(
      null
    );
    const [selectedTool, setSelectedTool] = useState<DrawingTool>("pen");
    const [selectedColor, setSelectedColor] = useState("var(--glass-black)");
    const [selectedSize, setSelectedSize] = useState(4);
    const [localStrokes, setLocalStrokes] = useState<DrawingStroke[]>(strokes);
    const [undoStack, setUndoStack] = useState<DrawingStroke[][]>([]);
    const [redoStack, setRedoStack] = useState<DrawingStroke[][]>([]);
    const [simulatedUsers, setSimulatedUsers] = useState(users);
    const [gridVisibleState, setGridVisible] = useState(gridVisible);
    const { play } = useGlassSound();
    const id = useA11yId("glass-shared-whiteboard");
    const { shouldAnimate } = useMotionPreference();

    // Helper function to respect motion preferences
    const respectMotionPreference = (config: Transition): Transition =>
      shouldAnimate ? config : { duration: 0 };

    const currentUser = users.find((u) => u.id === currentUserId);

    // Simulated collaborative activity
    useEffect(() => {
      if (!realTimeSync) return;

      const interval = setInterval(() => {
        setSimulatedUsers((prev) =>
          prev.map((user) => {
            if (user.id === currentUserId) return user;

            // Randomly move cursors and simulate drawing
            const deltaX = (Math.random() - 0.5) * 50;
            const deltaY = (Math.random() - 0.5) * 50;
            const newX = Math.max(0, Math.min(width, user.cursorX + deltaX));
            const newY = Math.max(0, Math.min(height, user.cursorY + deltaY));

            const isDrawing = Math.random() < 0.1;

            if (isDrawing && Math.random() < 0.3) {
              // Simulate a simple drawing stroke
              const stroke: DrawingStroke = {
                id: `stroke-${Date.now()}-${user.id}`,
                userId: user.id,
                userName: user.name,
                userColor: user.color,
                points: [
                  { x: newX, y: newY },
                  {
                    x: newX + Math.random() * 20,
                    y: newY + Math.random() * 20,
                  },
                ],
                tool: "pen",
                color: user.color,
                size: 3,
                opacity: 0.8,
                timestamp: Date.now(),
                isComplete: true,
              };

              setLocalStrokes((prev) => [
                ...prev.slice(-maxStrokes + 1),
                stroke,
              ]);
              onStroke?.(stroke);

              if (soundEnabled) {
                play("draw");
              }
            }

            onUserCursorMove?.(user.id, newX, newY);

            return {
              ...user,
              cursorX: newX,
              cursorY: newY,
              isDrawing,
              lastActivity: Date.now(),
            };
          })
        );
      }, 200);

      return () => clearInterval(interval);
    }, [
      realTimeSync,
      currentUserId,
      width,
      height,
      maxStrokes,
      onStroke,
      onUserCursorMove,
      soundEnabled,
      play,
    ]);

    // Draw on canvas
    const drawStrokes = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Clear canvas
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height);

      // Draw grid if visible
      if (gridVisibleState) {
        ctx.strokeStyle = "#E5E5E5";
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.3;

        for (let x = 0; x <= width; x += 20) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, height);
          ctx.stroke();
        }

        for (let y = 0; y <= height; y += 20) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(width, y);
          ctx.stroke();
        }
      }

      ctx.globalAlpha = 1;

      // Draw all strokes
      localStrokes.forEach((stroke) => {
        if (stroke.points.length < 2) return;

        ctx.strokeStyle = stroke.color;
        ctx.lineWidth = stroke.size;
        ctx.globalAlpha = stroke.opacity;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        if (stroke.tool === "eraser") {
          ctx.globalCompositeOperation = "destination-out";
        } else {
          ctx.globalCompositeOperation = "source-over";
        }

        ctx.beginPath();
        ctx.moveTo(stroke.points[0].x, stroke.points[0].y);

        for (let i = 1; i < stroke.points.length; i++) {
          ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }

        ctx.stroke();
      });

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
    }, [localStrokes, backgroundColor, gridVisibleState, width, height]);

    useEffect(() => {
      drawStrokes();
    }, [drawStrokes]);

    const startDrawing = useCallback(
      (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (readOnly || !canDraw) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        setIsDrawing(true);

        const stroke: DrawingStroke = {
          id: `stroke-${Date.now()}-${currentUserId}`,
          userId: currentUserId,
          userName: currentUser?.name || "Unknown",
          userColor: currentUser?.color || selectedColor,
          points: [{ x, y }],
          tool: selectedTool,
          color: selectedColor,
          size: selectedSize,
          opacity: selectedTool === "marker" ? 0.7 : 1,
          timestamp: Date.now(),
          isComplete: false,
        };

        setCurrentStroke(stroke);

        if (soundEnabled) {
          play("draw");
        }
      },
      [
        readOnly,
        canDraw,
        currentUserId,
        currentUser,
        selectedTool,
        selectedColor,
        selectedSize,
        soundEnabled,
        play,
      ]
    );

    const draw = useCallback(
      (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing || !currentStroke || readOnly) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const updatedStroke = {
          ...currentStroke,
          points: [...currentStroke.points, { x, y }],
        };

        setCurrentStroke(updatedStroke);
        setLocalStrokes((prev) => [
          ...prev.filter((s) => s.id !== updatedStroke.id),
          updatedStroke,
        ]);
      },
      [isDrawing, currentStroke, readOnly]
    );

    const stopDrawing = useCallback(() => {
      if (!currentStroke || readOnly) return;

      const completedStroke = {
        ...currentStroke,
        isComplete: true,
      };

      setLocalStrokes((prev) => {
        const newStrokes = [
          ...prev.filter((s) => s.id !== completedStroke.id),
          completedStroke,
        ].slice(-maxStrokes);

        setUndoStack((undoPrev) => [...undoPrev, prev]);
        setRedoStack([]);

        return newStrokes;
      });

      onStroke?.(completedStroke);
      setCurrentStroke(null);
      setIsDrawing(false);
    }, [currentStroke, readOnly, maxStrokes, onStroke]);

    const handleUndo = () => {
      if (undoStack.length === 0) return;

      const previousState = undoStack[undoStack.length - 1];
      setRedoStack((prev) => [localStrokes, ...prev]);
      setUndoStack((prev) => prev.slice(0, -1));
      setLocalStrokes(previousState);
      onUndo?.();
    };

    const handleRedo = () => {
      if (redoStack.length === 0) return;

      const nextState = redoStack[0];
      setUndoStack((prev) => [...prev, localStrokes]);
      setRedoStack((prev) => prev.slice(1));
      setLocalStrokes(nextState);
      onRedo?.();
    };

    const handleClear = () => {
      setUndoStack((prev) => [...prev, localStrokes]);
      setRedoStack([]);
      setLocalStrokes([]);
      onClear?.();
    };

    const UserCursor = ({ user }: { user: WhiteboardUser }) => (
      <motion.div
        className="glass-absolute glass-pointer-events-none glass-z-20"
        style={{
          left: user.cursorX,
          top: user.cursorY,
          transform: "translate(-2px, -2px)",
        }}
        animate={
          prefersReducedMotion
            ? {}
            : {
                scale: user.isDrawing ? 1.2 : 1,
                opacity: Date.now() - user.lastActivity < 5000 ? 1 : 0.5,
              }
        }
        transition={respectMotionPreference({
          type: "spring",
          stiffness: 400,
          damping: 30,
        })}
      >
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path
            d="M5 3L19 12L12 14L9 21L5 3Z"
            fill={user.color}
            stroke="white"
            strokeWidth="1"
          />
        </svg>
        <div
          className="glass-mt-2 glass-px-2 glass-py-1 glass-text-xs glass-font-medium glass-text-primary glass-radius"
          style={createGlassStyle({ variant: "default", radius: "sm" })}
        >
          {user.name}
        </div>
      </motion.div>
    );

    return (
      <OptimizedGlass
        ref={ref}
        intensity="subtle"
        className={cn("glass-relative", className)}
        {...props}
      >
        <div className="glass-flex glass-flex-col glass-space-y-4">
          {/* Toolbar */}
          {showToolbar && !readOnly && (
            <div
              className="glass-flex glass-items-center glass-justify-between glass-p-3 glass-radius-lg glass-flex-wrap glass-gap-3"
              style={createGlassStyle({ variant: "default", radius: "lg" })}
            >
              <div className="glass-flex glass-items-center glass-flex-wrap glass-gap-3">
                {/* Tools */}
                <div className="glass-flex glass-space-x-2">
                  {tools.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => setSelectedTool(tool.id)}
                      className={cn(
                        "glass-p-2 glass-radius glass-text-sm glass-font-medium glass-transition-colors glass-focus glass-touch-target glass-contrast-guard",
                        selectedTool === tool.id
                          ? "glass-surface-subtle/20 glass-text-primary"
                          : "glass-text-secondary"
                      )}
                      title={tool.name}
                    >
                      {tool.icon}
                    </button>
                  ))}
                </div>

                {/* Colors */}
                <div className="glass-flex glass-space-x-1">
                  {colors.map((color, i) => (
                    <button
                      key={`${color}-${i}`}
                      onClick={() => setSelectedColor(color)}
                      className={cn(
                        "glass-w-6 glass-h-6 glass-radius glass-border-2 glass-transition-transform glass-focus glass-touch-target glass-contrast-guard",
                        selectedColor === color
                          ? "glass-border-white"
                          : "glass-border-white/30"
                      )}
                      style={{
                        backgroundColor: color,
                        transform:
                          selectedColor === color ? "scale(1.1)" : undefined,
                      }}
                      aria-label={`Select color ${color}`}
                    />
                  ))}
                </div>

                {/* Sizes */}
                <div className="glass-flex glass-space-x-1">
                  {sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={cn(
                        "glass-w-8 glass-h-8 glass-radius glass-flex glass-items-center glass-justify-center glass-transition-colors glass-focus glass-touch-target glass-contrast-guard",
                        selectedSize === size
                          ? "glass-surface-subtle/20 glass-text-primary"
                          : "glass-text-secondary"
                      )}
                      aria-label={`Select brush size ${size}px`}
                    >
                      <div
                        className="glass-bg-transparent glass-radius-full"
                        style={{
                          width: Math.min(size, 16),
                          height: Math.min(size, 16),
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="glass-flex glass-items-center glass-flex-wrap glass-gap-2">
                {showUndoRedo && (
                  <>
                    <button
                      onClick={handleUndo}
                      disabled={undoStack.length === 0}
                      className="glass-px-3 glass-py-1 glass-text-sm glass-font-medium glass-text-primary-opacity-70 hover:glass-text-primary disabled:glass-opacity-50 glass-disabled-cursor-not-allowed glass-focus glass-touch-target glass-contrast-guard glass-focus glass-touch-target glass-contrast-guard"
                    >
                      ↶ Undo
                    </button>
                    <button
                      onClick={handleRedo}
                      disabled={redoStack.length === 0}
                      className="glass-px-3 glass-py-1 glass-text-sm glass-font-medium glass-text-primary-opacity-70 hover:glass-text-primary disabled:glass-opacity-50 glass-disabled-cursor-not-allowed glass-focus glass-touch-target glass-contrast-guard glass-focus glass-touch-target glass-contrast-guard"
                    >
                      ↷ Redo
                    </button>
                  </>
                )}
                <button
                  onClick={handleClear}
                  className="glass-px-3 glass-py-1 glass-text-sm glass-font-medium glass-text-primary hover:glass-text-secondary glass-focus glass-touch-target glass-contrast-guard glass-focus glass-touch-target glass-contrast-guard"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          <div className="glass-flex glass-flex-wrap glass-gap-4">
            {/* Whiteboard Canvas */}
            <div className="glass-relative glass-flex-1 glass-min-w-0">
              <canvas
                ref={canvasRef}
                width={width}
                height={height}
                className={cn(
                  "glass-border glass-border-white/20 glass-radius-lg glass-cursor-crosshair",
                  readOnly && "glass-cursor-not-allowed"
                )}
                style={{
                  width: "100%",
                  maxWidth: width,
                  height: "auto",
                  display: "block",
                }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
              />

              {/* User cursors */}
              {showUserCursors && (
                <AnimatePresence>
                  {simulatedUsers
                    .filter((user) => user.id !== currentUserId)
                    .map((user) => (
                      <UserCursor key={user.id} user={user} />
                    ))}
                </AnimatePresence>
              )}

              {/* Grid toggle */}
              {!readOnly && (
                <button
                  onClick={() => setGridVisible((visible) => !visible)}
                  className="glass-absolute glass-top-2 glass-right-2 glass-p-2 glass-text-primary-glass-opacity-60 hover:glass-text-primary glass-focus glass-touch-target glass-contrast-guard"
                  title="Toggle Grid"
                >
                  #
                </button>
              )}
            </div>

            {/* User List */}
            {showUserList && (
              <div
                className="glass-w-48 glass-p-3 glass-radius-lg glass-space-y-2"
                style={createGlassStyle({ variant: "default", radius: "lg" })}
              >
                <h3 className="glass-text-sm glass-font-medium glass-text-primary-glass-opacity-90 glass-mb-3">
                  Active Users ({simulatedUsers.length})
                </h3>
                {simulatedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="glass-flex glass-items-center glass-space-x-2 glass-p-2 glass-radius hover:glass-surface-subtle/5"
                  >
                    <div
                      className="glass-w-3 glass-h-3 glass-radius-full glass-border glass-border-white/30"
                      style={{ backgroundColor: user.color }}
                    />
                    <span className="glass-text-sm glass-text-primary-glass-opacity-80 glass-truncate">
                      {user.name}
                      {user.id === currentUserId && " (You)"}
                    </span>
                    {user.isDrawing && (
                      <span className="glass-text-xs glass-text-primary">
                        ✏️
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status */}
          <div className="glass-flex glass-items-center glass-justify-between glass-text-xs glass-text-primary-glass-opacity-50">
            <div className="glass-flex glass-items-center glass-space-x-4">
              <span>{localStrokes.length} strokes</span>
              {realTimeSync && (
                <span className="glass-flex glass-items-center glass-space-x-1">
                  <div className="glass-w-2 glass-h-2 glass-surface-success glass-radius-full glass-animate-pulse" />
                  <span>Synced</span>
                </span>
              )}
              {readOnly && (
                <span className="glass-text-primary">Read Only</span>
              )}
            </div>
            <div>
              Canvas: {width}×{height}
            </div>
          </div>
        </div>
      </OptimizedGlass>
    );
  }
);
